import { useState, useEffect } from 'react';
import { Settings2, Phone, BarChart3, Home, User, Building2, Info, Newspaper, Check } from 'lucide-react';
import { useFetch } from '../../hooks/useFetch';
import { settingsApi } from '../../api/contentApi';
import { Button } from '../../components/ui/Button';
import { MediaPicker } from '../../components/admin/MediaPicker';
import './SettingsManager.css';

const TABS = [
  { id: 'general', label: 'Général', icon: Settings2 },
  { id: 'coordonnees', label: 'Coordonnées & réseaux', icon: Phone },
  { id: 'analytics', label: 'Suivi & analytics', icon: BarChart3 },
  { id: 'accueil', label: 'Accueil', icon: Home },
  { id: 'particuliers', label: 'Particuliers', icon: User },
  { id: 'professionnels', label: 'Professionnels', icon: Building2 },
  { id: 'apropos', label: 'À propos', icon: Info },
  { id: 'actualites', label: 'Actualités', icon: Newspaper },
];

function Champ({ label, value, onChange, textarea, placeholder }) {
  return (
    <div className="admin-form__field">
      <label>{label}</label>
      {textarea ? (
        <textarea rows={2} value={value || ''} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input value={value || ''} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

export function SettingsManager() {
  const { data: settings, loading } = useFetch(() => settingsApi.adminGet(), []);
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    if (settings) setValues(settings);
  }, [settings]);

  function set(cle, val) {
    setSaved(false);
    setValues((prev) => ({ ...prev, [cle]: val }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setSaved(false);

    await settingsApi.update(values);

    setSubmitting(false);
    setSaved(true);
  }

  if (loading) return <p>Chargement...</p>;

  return (
    <section className="settings-page">
      <div className="admin-toolbar">
        <h1>Paramètres</h1>
      </div>

      <form className="settings-page__layout" onSubmit={handleSubmit}>
        <nav className="settings-tabs">
          {TABS.map((tab) => (
            <button
              type="button"
              key={tab.id}
              className={`settings-tabs__item ${activeTab === tab.id ? 'settings-tabs__item--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon size={17} />
              {tab.label}
            </button>
          ))}
        </nav>

        <div className="settings-panel">
          {activeTab === 'general' && (
            <div className="settings-panel__section">
              <h2>Général</h2>
              <p className="settings-panel__desc">Logo affiché partout sur le site (en-tête, pied de page, back-office).</p>

              <h3 className="settings-panel__subtitle">Logo</h3>
              <div className="settings-panel__grid">
                <MediaPicker
                  label="Logo sur fond clair (version sombre)"
                  value={values['site.logo_clair_url']}
                  onChange={(media) => set('site.logo_clair_url', media?.chemin || '')}
                />
                <MediaPicker
                  label="Logo sur fond sombre (version blanche)"
                  value={values['site.logo_sombre_url']}
                  onChange={(media) => set('site.logo_sombre_url', media?.chemin || '')}
                />
              </div>

              <h3 className="settings-panel__subtitle">Labels & certifications</h3>
              <p className="settings-panel__desc">
                Logos affichés dans le footer du site. Laisse vide pour ne pas afficher un logo.
              </p>
              <div className="settings-panel__grid">
                <MediaPicker
                  label="Logo FEDESAP"
                  value={values['footer.logo_fedesap_url']}
                  onChange={(media) => set('footer.logo_fedesap_url', media?.chemin || '')}
                />
                <MediaPicker
                  label="Logo Services à la Personne (SAP)"
                  value={values['footer.logo_sap_url']}
                  onChange={(media) => set('footer.logo_sap_url', media?.chemin || '')}
                />
              </div>

              <h3 className="settings-panel__subtitle">Mode maintenance</h3>
              <p className="settings-panel__desc">
                Une fois activé, les visiteurs voient uniquement le message ci-dessous à la place du site.
                Seul un compte du back-office connecté continue de voir le site normalement — tu peux te
                connecter directement depuis l'écran de maintenance.
              </p>
              <div className="admin-form__field admin-form__field--checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={values['maintenance.actif'] === '1'}
                    onChange={(e) => set('maintenance.actif', e.target.checked ? '1' : '0')}
                  />
                  Activer le mode maintenance
                </label>
              </div>
              <Champ
                label="Message affiché aux visiteurs"
                textarea
                placeholder="Nous améliorons actuellement notre site pour mieux vous servir. Merci de votre patience, nous serons de retour très prochainement."
                value={values['maintenance.message']}
                onChange={(v) => set('maintenance.message', v)}
              />

              <h3 className="settings-panel__subtitle">Bandeau cookies</h3>
              <p className="settings-panel__desc">
                Bandeau de consentement affiché aux visiteurs (RGPD). Tant qu'il est actif, les outils de
                suivi (Google Analytics, Google Tag Manager, Meta Pixel — onglet « Suivi &amp; analytics »)
                ne se chargent que si le visiteur donne son accord.
              </p>
              <div className="admin-form__field admin-form__field--checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={values['cookies.actif'] !== '0'}
                    onChange={(e) => set('cookies.actif', e.target.checked ? '1' : '0')}
                  />
                  Afficher le bandeau cookies
                </label>
              </div>
              <Champ
                label="Message affiché aux visiteurs"
                textarea
                placeholder="Nous n'utilisons aucun cookie de suivi par défaut. Avec votre accord, nous activons des outils de mesure d'audience et de publicité pour améliorer le site."
                value={values['cookies.message']}
                onChange={(v) => set('cookies.message', v)}
              />
            </div>
          )}

          {activeTab === 'coordonnees' && (
            <div className="settings-panel__section">
              <h2>Coordonnées & réseaux</h2>
              <p className="settings-panel__desc">Affichées dans le footer et la page Contact du site public.</p>

              <div className="settings-panel__grid">
                <Champ label="Téléphone" value={values['contact.telephone']} onChange={(v) => set('contact.telephone', v)} />
                <Champ label="Email" value={values['contact.email']} onChange={(v) => set('contact.email', v)} />
                <Champ label="Horaires" value={values['contact.horaires']} onChange={(v) => set('contact.horaires', v)} />
                <Champ label="Adresse" value={values['contact.adresse']} onChange={(v) => set('contact.adresse', v)} />
                <Champ label="Facebook" value={values['reseaux.facebook']} onChange={(v) => set('reseaux.facebook', v)} />
                <Champ label="Instagram" value={values['reseaux.instagram']} onChange={(v) => set('reseaux.instagram', v)} />
                <Champ label="LinkedIn" value={values['reseaux.linkedin']} onChange={(v) => set('reseaux.linkedin', v)} />
                <Champ label="TikTok" value={values['reseaux.tiktok']} onChange={(v) => set('reseaux.tiktok', v)} />
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="settings-panel__section">
              <h2>Suivi & analytics</h2>
              <p className="settings-panel__desc">
                Identifiants de suivi injectés automatiquement sur le site public, et lien d'intégration
                Google Maps affiché sur la page Contact.
              </p>

              <div className="settings-panel__grid">
                <Champ
                  label="Google Analytics ID"
                  placeholder="G-XXXXXXXXXX"
                  value={values['analytics.google_analytics_id']}
                  onChange={(v) => set('analytics.google_analytics_id', v)}
                />
                <Champ
                  label="Google Tag Manager ID"
                  placeholder="GTM-XXXXXXX"
                  value={values['analytics.google_tag_manager_id']}
                  onChange={(v) => set('analytics.google_tag_manager_id', v)}
                />
                <Champ
                  label="Pixel Meta"
                  placeholder="123456789012345"
                  value={values['analytics.meta_pixel_id']}
                  onChange={(v) => set('analytics.meta_pixel_id', v)}
                />
              </div>

              <Champ
                label="Google Maps (URL embed)"
                placeholder="https://www.google.com/maps/embed?pb=..."
                value={values['maps.google_maps_embed']}
                onChange={(v) => set('maps.google_maps_embed', v)}
              />
            </div>
          )}

          {activeTab === 'accueil' && (
            <div className="settings-panel__section">
              <h2>Page d'accueil</h2>
              <p className="settings-panel__desc">Titre, paragraphe et image du hero affiché en haut de l'accueil.</p>

              <Champ
                label="Phrase au-dessus du titre"
                value={values['hero.eyebrow']}
                onChange={(v) => set('hero.eyebrow', v)}
              />
              <Champ label="Titre du hero" value={values['hero.titre']} onChange={(v) => set('hero.titre', v)} />
              <Champ
                label="Paragraphe sous le titre"
                textarea
                value={values['hero.sous_titre']}
                onChange={(v) => set('hero.sous_titre', v)}
              />
              <MediaPicker
                label="Image du hero"
                value={values['hero.image_url']}
                onChange={(media) => set('hero.image_url', media?.chemin || '')}
              />

              <h3 className="settings-panel__subtitle">Zone d'intervention</h3>
              <Champ
                label="Une zone par ligne."
                textarea
                placeholder="Nous intervenons en Île-de-France et dans les grandes métropoles limitrophes."
                value={values['zones.description']}
                onChange={(v) => set('zones.description', v)}
              />
            </div>
          )}

          {activeTab === 'particuliers' && (
            <div className="settings-panel__section">
              <h2>Univers Particuliers</h2>
              <p className="settings-panel__desc">Titre, paragraphe et image du hero affiché sur la page Particuliers.</p>

              <Champ
                label="Phrase au-dessus du titre"
                value={values['particulier.hero_eyebrow']}
                onChange={(v) => set('particulier.hero_eyebrow', v)}
              />
              <Champ
                label="Titre du hero"
                value={values['particulier.hero_titre']}
                onChange={(v) => set('particulier.hero_titre', v)}
              />
              <Champ
                label="Paragraphe sous le titre"
                textarea
                value={values['particulier.hero_sous_titre']}
                onChange={(v) => set('particulier.hero_sous_titre', v)}
              />
              <MediaPicker
                label="Image du hero"
                value={values['particulier.hero_image_url']}
                onChange={(media) => set('particulier.hero_image_url', media?.chemin || '')}
              />
            </div>
          )}

          {activeTab === 'professionnels' && (
            <div className="settings-panel__section">
              <h2>Univers Professionnels</h2>
              <p className="settings-panel__desc">Titre, paragraphe et image du hero affiché sur la page Professionnels.</p>

              <Champ
                label="Phrase au-dessus du titre"
                value={values['professionnel.hero_eyebrow']}
                onChange={(v) => set('professionnel.hero_eyebrow', v)}
              />
              <Champ
                label="Titre du hero"
                value={values['professionnel.hero_titre']}
                onChange={(v) => set('professionnel.hero_titre', v)}
              />
              <Champ
                label="Paragraphe sous le titre"
                textarea
                value={values['professionnel.hero_sous_titre']}
                onChange={(v) => set('professionnel.hero_sous_titre', v)}
              />
              <MediaPicker
                label="Image du hero"
                value={values['professionnel.hero_image_url']}
                onChange={(media) => set('professionnel.hero_image_url', media?.chemin || '')}
              />
            </div>
          )}

          {activeTab === 'apropos' && (
            <div className="settings-panel__section">
              <h2>Page À propos</h2>
              <p className="settings-panel__desc">Titre, paragraphe et image du hero affiché sur la page À propos.</p>

              <Champ
                label="Phrase au-dessus du titre"
                value={values['apropos.hero_eyebrow']}
                onChange={(v) => set('apropos.hero_eyebrow', v)}
              />
              <Champ
                label="Titre du hero"
                value={values['apropos.hero_titre']}
                onChange={(v) => set('apropos.hero_titre', v)}
              />
              <Champ
                label="Paragraphe sous le titre"
                textarea
                value={values['apropos.hero_sous_titre']}
                onChange={(v) => set('apropos.hero_sous_titre', v)}
              />
              <MediaPicker
                label="Image du hero"
                value={values['apropos.hero_image_url']}
                onChange={(media) => set('apropos.hero_image_url', media?.chemin || '')}
              />
            </div>
          )}

          {activeTab === 'actualites' && (
            <div className="settings-panel__section">
              <h2>Actualités</h2>
              <p className="settings-panel__desc">Titre, paragraphe et image du hero affiché sur la page Actualités.</p>

              <Champ
                label="Phrase au-dessus du titre"
                value={values['blog.hero_eyebrow']}
                onChange={(v) => set('blog.hero_eyebrow', v)}
              />
              <Champ
                label="Titre du hero"
                value={values['blog.hero_titre']}
                onChange={(v) => set('blog.hero_titre', v)}
              />
              <Champ
                label="Paragraphe sous le titre"
                textarea
                value={values['blog.hero_sous_titre']}
                onChange={(v) => set('blog.hero_sous_titre', v)}
              />
              <MediaPicker
                label="Image du hero"
                value={values['blog.hero_image_url']}
                onChange={(media) => set('blog.hero_image_url', media?.chemin || '')}
              />
            </div>
          )}

          <div className="settings-panel__actions">
            {saved && (
              <span className="settings-panel__saved">
                <Check size={16} /> Paramètres enregistrés
              </span>
            )}
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      </form>
    </section>
  );
}
